import React, {useEffect, useRef, useState, useMemo} from 'react';
import PropTypes from "prop-types";
import {
  errorMessage,
  getEffectDependencies,
  getValue,
  isInvalid,
  onBlur,
  onChange
} from "./Component";
import {parseFloat, DECIMAL_SEPARATOR} from "../../utils/NumberUtils";

const Input = props => {
  const [value, setValue] = useState('');
  const inputRef = useRef();

  const [decimalHasBeenFormatted, setDecimalHasBeenFormatted] = useState(false);

  const numberRegex = useMemo(() => {
    if (props.type !== "number") {
      return null;
    }

    const {negative, decimalPlaces} = props.numberFormat;

    const regexMap = {
      //_N_ and _X_ are placeholders for negative sign and decimal places number
      ".": "^_N_\\d*\\.?\\d{0,_X_}$", //decimal numbers with DOT separator.
      ",": "^_N_\\d*,?\\d{0,_X_}$", //decimal numbers with COMMA separator
      "integer": "^_N_\\d*$", //integer numbers
    }

    const regex = regexMap[decimalPlaces ? DECIMAL_SEPARATOR : "integer"];
    return new RegExp(regex.replace("_X_", decimalPlaces).replace("_N_", negative ? "-?" : ""))
  }, []);

  const regExp = useMemo(() => {
    if (props.regex === null) {
      return null;
    }

    return new RegExp(props.regex)
  }, []);

  useEffect(() => {
    if (props.type !== "number") {
      if (!!props.prefix && !getValue(props).startsWith(props.prefix)) {
        setValue(props.prefix + getValue(props));
        return;
      } else {
        setValue(getValue(props));
      }
      return;
    } else {
        if (props.prefix && getValue(props).toString().startsWith(props.prefix)) {
          console.warn("Input component should not specify prefix " + props.prefix + " or start with prefix when type is" + props.type , getValue(props));
        }
      setValue(getValue(props));
    }

    let val = getValue(props)

    // format existing value to N decimals
    const decimalPlaces = props.numberFormat.decimalPlaces;
    if (props.formatDecimalNumber && decimalPlaces > 0) {
      const parsedValue = parseFloat(val)
      if (decimalPlaces > 0 && !decimalHasBeenFormatted && parsedValue != null) {
        val = parsedValue.toFixed(decimalPlaces);
        setDecimalHasBeenFormatted(true);
      }
    }
    setValue(props.prefix + (DECIMAL_SEPARATOR === "," ? val.toString().replace(".", ",") : val));
  }, [...getEffectDependencies(props)]);

  useEffect(() => {
    props.focus && inputRef.current?.focus && inputRef.current.focus();
  }, [props.focus]);

  function onInputValueChange(event) {
    const inputValue = event.target.value;

    // Prevent the prefix from being removed or altered
    if (!inputValue.startsWith(props.prefix)) {
      setValue(props.prefix);
      onChange(props, props.prefix);

      return;
    }
    const actualValue = inputValue.replace(props.prefix, '');

    const value = props.type === "number" ? parseFloat(actualValue.replaceAll(",", ".")) : (actualValue);
    if (props.type === "number") {
      // Test more for numbers when prefix is present.
      setValue(value);
      onChange(props, value);
      return;
    }
    setValue(props.prefix + actualValue);
    onChange(props, props.prefix + actualValue);
  }

  const onUpArrow = () => {
    const newValue = +value.replace(props.prefix, '') + 1;
    if (newValue === +props.max || newValue > +props.max)
      return;
    setValue(props.prefix + (newValue));
    onChange(props, newValue);
  }
  const onDownArrow = () => {
    const newValue = +value.replace(props.prefix, '') - 1;
    if (newValue < +props.min || (props.numberFormat.negative === false && newValue < 0))
      return;
    setValue(props.prefix + (newValue));
    onChange(props, newValue);
  }

  function validate(event) {
    //construct input value after change
    const inputValueAfterEvent =
        event.target.value.substr(0, event.target.selectionStart) +
        event.data +
        event.target.value.substr(event.target.selectionEnd);
    if (props.regex && regExp && !regExp.test(inputValueAfterEvent.replace(props.prefix, ''))) {
      event.preventDefault();
    }
    if (numberRegex && !numberRegex.test(inputValueAfterEvent.replace(props.prefix, ''))) {
      event.preventDefault();
    }
  }

  function onFocus() {
    if (typeof props.onFocus !== 'undefined') {
      props.onFocus();
    }
    // prevent decimal number formatting while typing
    if (props.type === "number" && props.formatDecimalNumber) {
      setDecimalHasBeenFormatted(true);
    }
  }

  function formatOnBlur() {
    if (props.type === "number" && props.formatDecimalNumber) {
      const val = parseFloat(value.replace(props.prefix, '').toString().replace(",", ".")).toFixed(props.numberFormat.decimalPlaces);
      setValue(props.prefix + (DECIMAL_SEPARATOR === "," ? val.toString().replace(".", ",") : val));
    }
    onBlur(props);
  }

  return (
      <label className={"field has-float-label loadable " + (props.className || "")}>
        <input
            id={props.id}
            name={props.id}
            value={value}
            disabled={props.disabled}
            type={"text"}
            min={props.min}
            max={props.max}
            ref={inputRef}
            placeholder=" "
            autoComplete={props.autoComplete}
            pattern={props.pattern}
            maxLength={props.maxLength}
            className={!props.skipValidation && isInvalid(props) ? "is-invalid input" : "input"}
            onBeforeInput={(numberRegex || props.regex != null) ? validate : () => {}}
            onChange={props.change ? props.change : onInputValueChange}
            onBlur={formatOnBlur}
            onFocus={onFocus}
            onKeyDown={props.onKeyDown}
        />
        <span>{props.label}</span>
        <div className={"error-icon fz fz-exclamation-triangle solid " + (props.showArrows ? "arrows-shift": null)}>
          <div className="has-tooltip danger" data-tooltip={errorMessage(props)}/>
        </div>
        {props.unit &&
            <div className="right-decoration is-text">{props.unit}</div>}
        {props.icon && (
            <div className={`right-decoration fz ${props.icon}`}></div>)}
        {props.showArrows
            ? <div className={"arrow-container " + (props.disabled ? "disabled": "")} onMouseDown={(event) => {
              event.preventDefault();
              if (props.onUpArrow) {
                props.onUpArrow();
                return;
              }
              onUpArrow();
            }}>
              <div className="arrow arrow-up"></div>
            </div>
            : null
        }
        {props.showArrows
            ? <div className={"arrow-container " + (props.disabled ? "disabled": "")} onMouseDown={(event) => {
              event.preventDefault();
              if (props.onDownArrow) {
                props.onDownArrow();
                return;
              }
              onDownArrow();
            }}>
              <div className="arrow arrow-down"></div>
            </div>
            : null
        }
      </label>
  );
}

Input.propTypes = {
  id: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  type: PropTypes.oneOf(["text", "number", "time", "email"]),
  className: PropTypes.string,
  label: PropTypes.string,
  unit: PropTypes.string,
  icon: PropTypes.string,
  disabled: PropTypes.bool,
  focus: PropTypes.bool,
  isInvalid: PropTypes.bool,
  numberFormat: PropTypes.shape({
    negative: PropTypes.bool,
    decimalPlaces: PropTypes.number,
  }),
  skipValidation: PropTypes.bool,
  invalidMessage: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  formField: PropTypes.object,
  maxLength: PropTypes.number,
  formatDecimalNumber: PropTypes.bool,
  prefix: PropTypes.string,
  onKeyDown: PropTypes.func,
  regex: PropTypes.string,
  autoComplete: PropTypes.string,
};

Input.defaultProps = {
  type: "text",
  numberFormat: {
    negative: true,
    decimalPlaces: 2,
  },
  prefix: '',
  onKeyDown: () => {},
};

export default Input;
